import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import config from '../config';
import { ROLES, roleFromGroups } from './roles';

// When Cognito vars are empty, run in local dev mode (no auth required by backend)
const isMfaDemo = process.env.NODE_ENV !== 'production' && config.mfaDemo;
const isLocalDev = isMfaDemo || !config.cognito.userPoolId || !config.cognito.clientId;

const userPool = isLocalDev
  ? null
  : new CognitoUserPool({
      UserPoolId: config.cognito.userPoolId,
      ClientId: config.cognito.clientId,
    });

let authVersion = 0;

function currentSessionUser() {
  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) return null;

  const version = authVersion;
  const storage = cognitoUser.storage;
  cognitoUser.storage = {
    getItem: key => storage.getItem(key),
    setItem: (key, value) => {
      if (version === authVersion) return storage.setItem(key, value);
    },
    removeItem: key => {
      if (version === authVersion) return storage.removeItem(key);
    },
  };
  return cognitoUser;
}

function sessionResult(session) {
  return {
    token: session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    user: parseSession(session),
  };
}

function authChallenge(code, cognitoUser) {
  const error = new Error(code);
  error.code = code;
  error.cognitoUser = cognitoUser;
  return error;
}

function authenticationCallbacks(cognitoUser, resolve, reject) {
  return {
    onSuccess: (session) => resolve(sessionResult(session)),
    onFailure: reject,
    newPasswordRequired: (userAttributes) => {
      const error = authChallenge('NEW_PASSWORD_REQUIRED', cognitoUser);
      error.userAttributes = userAttributes;
      reject(error);
    },
    mfaSetup: () => reject(authChallenge('MFA_SETUP_REQUIRED', cognitoUser)),
    totpRequired: () => reject(authChallenge('TOTP_REQUIRED', cognitoUser)),
    mfaRequired: () => reject(new Error('Unsupported MFA method')),
    selectMFAType: () => reject(new Error('Unsupported MFA selection')),
    customChallenge: () => reject(new Error('Unsupported authentication challenge')),
  };
}

// ─── Local dev helpers ───────────────────────────────────────────────
const LOCAL_USER_KEY = 'chatty_local_user';
const MFA_DEMO_USER_KEY = 'chatty_mfa_demo_user';
const LOCAL_PASSWORD_CHANGED_PREFIX = 'chatty_local_password_changed_';

function getMfaDemoUser() {
  const username = sessionStorage.getItem(MFA_DEMO_USER_KEY);
  return username ? { username, groups: ['user'], role: 'user' } : null;
}

function localPasswordChangedKey(username) {
  return `${LOCAL_PASSWORD_CHANGED_PREFIX}${username.trim().toLowerCase()}`;
}

function getLocalPasswordChangedAt(username) {
  const value = Number(localStorage.getItem(localPasswordChangedKey(username)));
  return Number.isFinite(value) && value > 0 ? value : null;
}

function setLocalPasswordChangedAt(username) {
  localStorage.setItem(localPasswordChangedKey(username), String(Date.now()));
}

function getLocalUser() {
  try {
    return JSON.parse(sessionStorage.getItem(LOCAL_USER_KEY));
  } catch {
    return null;
  }
}

function setLocalUser(user) {
  sessionStorage.setItem(LOCAL_USER_KEY, JSON.stringify(user));
}

function clearLocalUser() {
  sessionStorage.removeItem(LOCAL_USER_KEY);
}

// ─── Exported auth functions ─────────────────────────────────────────

// "alice:hr" -> { username: 'alice', role: 'hr' }; no Role suffix means full admin, like the backend.
function parseLocalLogin(login) {
  const separator = login.lastIndexOf(':');
  const role = login.slice(separator + 1);
  if (separator > 0 && ROLES.includes(role)) {
    return { username: login.slice(0, separator), role, groups: [role] };
  }
  return { username: login, role: 'admin', groups: ['admin', 'user'] };
}

/**
 * Authenticate user.
 * - Local dev: accepts any username/password. Admin by default; "name:<role>" picks a Role.
 * - Production: authenticates against Cognito.
 */
export function login(username, password) {
  authVersion += 1;
  if (isMfaDemo) {
    return Promise.reject(new Error('Use the local MFA demo screen'));
  }
  if (isLocalDev) {
    const localLogin = parseLocalLogin(username);
    let changedAt = getLocalPasswordChangedAt(localLogin.username);
    if (!changedAt) {
      setLocalPasswordChangedAt(localLogin.username);
      changedAt = Date.now();
    }
    const rotationMilliseconds = config.passwordRotationTestMinutes * 60_000;
    const passwordChangeRequired =
      rotationMilliseconds > 0 && Date.now() >= changedAt + rotationMilliseconds;
    const user = { ...localLogin, passwordChangeRequired };
    setLocalUser(user);
    return Promise.resolve({ token: null, accessToken: null, user });
  }

  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });
    cognitoUser.setAuthenticationFlowType('USER_PASSWORD_AUTH');

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    cognitoUser.authenticateUser(
      authDetails,
      authenticationCallbacks(cognitoUser, resolve, reject)
    );
  });
}

export function disableRestrictedSessionRefresh() {
  // A restricted login may use its current one-hour tokens only to change the
  // password. Removing the cached refresh token prevents it from becoming an
  // unrestricted refreshed session, while sessions created before expiry keep
  // their refresh token and continue normally.
  if (!isLocalDev) {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      const keyPrefix = `CognitoIdentityServiceProvider.${config.cognito.clientId}.${cognitoUser.getUsername()}`;
      userPool.storage.removeItem(`${keyPrefix}.refreshToken`);
    }
  }
}

export function completeLocalPasswordRotation(username) {
  if (!isLocalDev) {
    throw new Error('Local password rotation is unavailable with Cognito enabled');
  }
  setLocalPasswordChangedAt(username);
}

export function isLocalAuthentication() {
  return isLocalDev;
}

export function isMfaDemoEnabled() {
  return isMfaDemo;
}

export function completeMfaDemoLogin(username) {
  if (!isMfaDemo) throw new Error('Local MFA demo is disabled');
  sessionStorage.setItem(MFA_DEMO_USER_KEY, username);
  return getMfaDemoUser();
}

/**
 * Complete the new-password challenge after first login with temp password.
 */
export function completeNewPassword(cognitoUser, newPassword) {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(
      newPassword,
      {},
      authenticationCallbacks(cognitoUser, resolve, reject)
    );
  });
}

export function associateTotp(cognitoUser) {
  return new Promise((resolve, reject) => {
    cognitoUser.associateSoftwareToken({
      associateSecretCode: resolve,
      onFailure: reject,
    });
  });
}

export function verifyTotpSetup(cognitoUser, code) {
  return new Promise((resolve, reject) => {
    cognitoUser.verifySoftwareToken(code, 'Chatty authenticator', {
      onSuccess: (session) => resolve(sessionResult(session)),
      onFailure: reject,
    });
  });
}

export function completeTotpChallenge(cognitoUser, code) {
  return new Promise((resolve, reject) => {
    cognitoUser.sendMFACode(code, {
      onSuccess: (session) => resolve(sessionResult(session)),
      onFailure: reject,
    }, 'SOFTWARE_TOKEN_MFA');
  });
}

/**
 * Get a valid ID token, refreshing if needed.
 * Returns the JWT string or null if not authenticated.
 */
export function getToken() {
  if (isMfaDemo) {
    const user = getMfaDemoUser();
    return Promise.resolve(user?.username ? `local-mfa-demo:${encodeURIComponent(user.username)}` : null);
  }
  if (isLocalDev) {
    const localUser = getLocalUser();
    if (!localUser?.username) return Promise.resolve(null);
    // Mirror the backend's optional ":<role>" suffix; without it the backend grants admin.
    const roleSuffix = localUser.role && localUser.role !== 'admin' ? `:${localUser.role}` : '';
    return Promise.resolve(`local-dev:${encodeURIComponent(localUser.username)}${roleSuffix}`);
  }

  return new Promise((resolve) => {
    const version = authVersion;
    const cognitoUser = currentSessionUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (version !== authVersion || err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

/**
 * Get the current authenticated user info.
 * Returns { username, groups, role } or null.
 */
export function getCurrentUser() {
  if (isMfaDemo) {
    return Promise.resolve(getMfaDemoUser());
  }
  if (isLocalDev) {
    return Promise.resolve(getLocalUser());
  }

  return new Promise((resolve) => {
    const version = authVersion;
    const cognitoUser = currentSessionUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (version !== authVersion || err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(parseSession(session));
    });
  });
}

export function refreshCurrentUser() {
  if (isMfaDemo) {
    return Promise.resolve(getMfaDemoUser());
  }

  if (isLocalDev) {
    return Promise.resolve(getLocalUser());
  }

  return new Promise((resolve, reject) => {
    const version = authVersion;
    const cognitoUser = currentSessionUser();

    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (
        version !== authVersion ||
        err?.message === 'Cannot retrieve a new session. Please authenticate.'
      ) {
        resolve(null);
        return;
      }
      if (err) {
        reject(err);
        return;
      }
      if (!session || !session.isValid()) {
        resolve(null);
        return;
      }

      const refreshToken = session.getRefreshToken();

      if (!refreshToken?.getToken()) {
        resolve(null);
        return;
      }

      cognitoUser.refreshSession(refreshToken, (refreshErr, newSession) => {
        if (version !== authVersion) {
          resolve(null);
          return;
        }
        if (refreshErr || !newSession) {
          reject(refreshErr || new Error('Session refresh returned no session'));
          return;
        }
        resolve(parseSession(newSession));
      });
    });
  });
}

/**
 * Sign out the current user and clear local tokens.
 */
export function logout() {
  authVersion += 1;
  if (isLocalDev) {
    clearLocalUser();
    if (isMfaDemo) sessionStorage.removeItem(MFA_DEMO_USER_KEY);
    return;
  }

  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
}

/**
 * Parse a Cognito session into a user info object.
 */
function parseSession(session) {
  const payload = session.getIdToken().decodePayload();
  const groups = payload['cognito:groups'] || [];
  return {
    username: payload['cognito:username'] || payload['sub'],
    groups,
    role: roleFromGroups(groups),
    passwordChangeRequired: payload.password_change_required === 'true',
  };
}
